/**
 * 가로 태그 팔레트 컴포넌트
 * @description 템플릿 에디터 하단에 그룹별 행으로 배치되는 태그 버튼들
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import type { PlaceholderConfig } from "../../types/sms.types";

interface TagPaletteHorizontalProps {
  /** 플레이스홀더 목록 */
  placeholders: PlaceholderConfig[];
  /** 태그 선택 핸들러 */
  onSelect: (config: PlaceholderConfig) => void;
  /** 추가 CSS 클래스 */
  className?: string;
}

export function TagPaletteHorizontal({
  placeholders,
  onSelect,
  className,
}: TagPaletteHorizontalProps) {
  const groupedPlaceholders = useMemo(() => {
    const groups: Record<string, PlaceholderConfig[]> = {};
    placeholders.forEach((config) => {
      const category = config.category || "기타";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(config);
    });
    return groups;
  }, [placeholders]);

  const categories = Object.keys(groupedPlaceholders);

  return (
    <div className={cn("border-t bg-muted/30", className)}>
      {/* 헤더 */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b">
        <span className="text-[10px] font-medium text-muted-foreground">
          태그 (클릭하면 삽입)
        </span>
        <span className="text-[10px] text-muted-foreground">
          또는 에디터에서 / 입력
        </span>
      </div>

      {/* 그룹별 행 */}
      <div className="max-h-[180px] overflow-y-auto">
        {categories.map((category) => {
          const configs = groupedPlaceholders[category];
          const categoryColor = configs[0]?.color;

          return (
            <div
              key={category}
              className="flex items-center border-b last:border-b-0"
            >
              {/* 카테고리 라벨 */}
              <div
                className="w-16 shrink-0 px-2 py-1.5 text-[11px] font-medium text-center border-r"
                style={{ color: categoryColor }}
              >
                {category}
              </div>

              {/* 태그 버튼들 */}
              <div className="flex-1 flex flex-wrap gap-1 p-1.5 overflow-x-auto">
                {configs.map((config) => (
                  <button
                    key={config.placeholder}
                    type="button"
                    title={config.description}
                    onClick={() => onSelect(config)}
                    className={cn(
                      "inline-flex items-center gap-1 px-2 py-0.5 rounded",
                      "text-[11px] font-medium whitespace-nowrap",
                      "border bg-background hover:bg-muted",
                      "transition-colors cursor-pointer",
                      "focus:outline-none focus:ring-1 focus:ring-ring",
                    )}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: config.color }}
                    />
                    {config.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TagPaletteHorizontal;
